import React, { useState } from "react";
import { X, ChevronDown, ChevronUp, Plane, Hotel } from "lucide-react";
import supabase from "../supabaseClient";

const TripsTab = ({ members, vacationBlocks, onReload }) => {
  const [adding, setAdding] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [label, setLabel] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [tripType, setTripType] = useState("play");
  const [showFlightSection, setShowFlightSection] = useState(false);
  const [outbound, setOutbound] = useState({ airline: "", number: "", departure: "", arrival: "" });
  const [returnFlight, setReturnFlight] = useState({ airline: "", number: "", departure: "", arrival: "" });
  const [accommodation, setAccommodation] = useState("");

  const resetForm = () => {
    setSelectedMemberIds([]);
    setLabel("");
    setStartDate("");
    setEndDate("");
    setTripType("play");
    setShowFlightSection(false);
    setOutbound({ airline: "", number: "", departure: "", arrival: "" });
    setReturnFlight({ airline: "", number: "", departure: "", arrival: "" });
    setAccommodation("");
  };

  const toggleMember = (id) => {
    setSelectedMemberIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleAdd = async () => {
    if (selectedMemberIds.length === 0 || !startDate || !endDate) {
      alert("Please select at least one traveler and both dates");
      return;
    }
    const hasFlightData =
      outbound.airline || outbound.number || outbound.departure || outbound.arrival ||
      returnFlight.airline || returnFlight.number || returnFlight.departure || returnFlight.arrival;
    try {
      const { error } = await supabase.from("vacation_blocks").insert([{
        member_ids: selectedMemberIds,
        member_id: selectedMemberIds[0],
        start_date: startDate,
        end_date: endDate,
        label: label.trim() || null,
        trip_type: tripType,
        flight_details: hasFlightData ? { outbound, return: returnFlight } : null,
        accommodation: accommodation.trim() || null,
      }]);
      if (error) throw error;
      setAdding(false);
      resetForm();
      await onReload();
    } catch (error) {
      console.error("Error adding trip:", error);
      alert("Error adding trip: " + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this trip?")) return;
    try {
      const { error } = await supabase.from("vacation_blocks").delete().eq("id", id);
      if (error) throw error;
      await onReload();
    } catch (error) {
      console.error("Error deleting trip:", error);
      alert("Error deleting: " + error.message);
    }
  };

  const getTripMembers = (block) => {
    const ids = block.member_ids || (block.member_id ? [block.member_id] : []);
    return ids.map((id) => members.find((m) => m.id === id)).filter(Boolean);
  };

  const formatDateTime = (dt) => {
    if (!dt) return "";
    try {
      return new Date(dt).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch {
      return dt;
    }
  };

  const sorted = [...(vacationBlocks || [])].sort((a, b) => a.start_date.localeCompare(b.start_date));

  return (
    <div className="space-y-3">
      {sorted.length === 0 && !adding && (
        <p className="text-sm text-gray-500 text-center py-4">No trips yet</p>
      )}

      {sorted.map((block) => {
        const tripMembers = getTripMembers(block);
        const isExpanded = expandedId === block.id;
        const fd = block.flight_details;
        const isWork = block.trip_type === "work";
        const hasDetails = fd || block.accommodation;

        return (
          <div key={block.id} className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="flex items-center gap-2 p-3">
              <div className="flex gap-1 flex-shrink-0">
                {tripMembers.map((m) => (
                  <div key={m.id} className="w-3 h-3 rounded-full" style={{ backgroundColor: m.color }} />
                ))}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-gray-800">
                    {tripMembers.map((m) => m.name).join(", ") || "Unknown"}
                  </span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                    isWork ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"
                  }`}>
                    {isWork ? "Work" : "Play"}
                  </span>
                </div>
                <div className="text-xs font-medium text-gray-700">{block.label || "Trip"}</div>
                <div className="text-xs text-gray-500">{block.start_date} → {block.end_date}</div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {hasDetails && (
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : block.id)}
                    className="p-1.5 text-gray-500 hover:bg-gray-100 rounded touch-manipulation"
                  >
                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                )}
                <button
                  onClick={() => handleDelete(block.id)}
                  className="p-1.5 text-red-600 hover:bg-red-50 rounded touch-manipulation"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {isExpanded && (
              <div className="px-3 pb-3 space-y-2 border-t border-gray-100 pt-2">
                {fd?.outbound && (fd.outbound.airline || fd.outbound.number || fd.outbound.departure || fd.outbound.arrival) && (
                  <div className="text-xs space-y-0.5">
                    <div className="font-medium text-gray-700 flex items-center gap-1">
                      <Plane size={12} /> Outbound flight
                    </div>
                    {fd.outbound.airline && <div className="text-gray-600 pl-4">Airline: {fd.outbound.airline}</div>}
                    {fd.outbound.number && <div className="text-gray-600 pl-4">Flight #: {fd.outbound.number}</div>}
                    {fd.outbound.departure && <div className="text-gray-600 pl-4">Departs: {formatDateTime(fd.outbound.departure)}</div>}
                    {fd.outbound.arrival && <div className="text-gray-600 pl-4">Arrives: {formatDateTime(fd.outbound.arrival)}</div>}
                  </div>
                )}
                {fd?.return && (fd.return.airline || fd.return.number || fd.return.departure || fd.return.arrival) && (
                  <div className="text-xs space-y-0.5">
                    <div className="font-medium text-gray-700 flex items-center gap-1">
                      <Plane size={12} style={{ transform: "scaleX(-1)" }} /> Return flight
                    </div>
                    {fd.return.airline && <div className="text-gray-600 pl-4">Airline: {fd.return.airline}</div>}
                    {fd.return.number && <div className="text-gray-600 pl-4">Flight #: {fd.return.number}</div>}
                    {fd.return.departure && <div className="text-gray-600 pl-4">Departs: {formatDateTime(fd.return.departure)}</div>}
                    {fd.return.arrival && <div className="text-gray-600 pl-4">Arrives: {formatDateTime(fd.return.arrival)}</div>}
                  </div>
                )}
                {block.accommodation && (
                  <div className="text-xs space-y-0.5">
                    <div className="font-medium text-gray-700 flex items-center gap-1">
                      <Hotel size={12} /> Accommodation
                    </div>
                    <div className="text-gray-600 pl-4 whitespace-pre-wrap">{block.accommodation}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {adding ? (
        <div className="p-3 border-2 border-purple-300 rounded-lg space-y-3">

          {/* Travelers */}
          <div>
            <div className="text-xs font-medium text-gray-600 mb-1.5">Travelers</div>
            <div className="flex flex-wrap gap-2">
              {members.map((m) => {
                const selected = selectedMemberIds.includes(m.id);
                return (
                  <button
                    key={m.id}
                    onClick={() => toggleMember(m.id)}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-sm touch-manipulation transition-colors ${
                      selected ? "text-white" : "border-gray-300 text-gray-700 bg-white"
                    }`}
                    style={selected ? { backgroundColor: m.color, borderColor: m.color } : {}}
                  >
                    <div
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: selected ? "rgba(255,255,255,0.6)" : m.color }}
                    />
                    {m.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Label */}
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Trip name (e.g., NYC Work Trip)"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base"
          />

          {/* Dates */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Start date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); if (endDate && e.target.value > endDate) setEndDate(""); }}
                className="w-full px-2 py-2 border border-gray-300 rounded-lg text-base"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">End date</label>
              <input
                type="date"
                value={endDate}
                min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-2 py-2 border border-gray-300 rounded-lg text-base"
              />
            </div>
          </div>

          {/* Trip type */}
          <div>
            <div className="text-xs font-medium text-gray-600 mb-1.5">Trip type</div>
            <div className="flex gap-2">
              <button
                onClick={() => setTripType("play")}
                className={`flex-1 py-2 rounded-lg text-sm font-medium touch-manipulation border transition-colors ${
                  tripType === "play"
                    ? "bg-green-600 text-white border-green-600"
                    : "bg-white text-gray-600 border-gray-300"
                }`}
              >
                Play / Vacation
              </button>
              <button
                onClick={() => setTripType("work")}
                className={`flex-1 py-2 rounded-lg text-sm font-medium touch-manipulation border transition-colors ${
                  tripType === "work"
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-600 border-gray-300"
                }`}
              >
                Work Travel
              </button>
            </div>
          </div>

          {/* Flight details */}
          <button
            onClick={() => setShowFlightSection(!showFlightSection)}
            className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600 touch-manipulation"
          >
            <span className="flex items-center gap-2"><Plane size={14} /> Flight details (optional)</span>
            {showFlightSection ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          {showFlightSection && (
            <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
              <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Outbound</div>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  value={outbound.airline}
                  onChange={(e) => setOutbound((p) => ({ ...p, airline: e.target.value }))}
                  placeholder="Airline"
                  className="px-2 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <input
                  type="text"
                  value={outbound.number}
                  onChange={(e) => setOutbound((p) => ({ ...p, number: e.target.value }))}
                  placeholder="Flight #"
                  className="px-2 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Departure</label>
                  <input
                    type="datetime-local"
                    value={outbound.departure}
                    onChange={(e) => setOutbound((p) => ({ ...p, departure: e.target.value }))}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Arrival</label>
                  <input
                    type="datetime-local"
                    value={outbound.arrival}
                    onChange={(e) => setOutbound((p) => ({ ...p, arrival: e.target.value }))}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>

              <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mt-1">Return</div>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  value={returnFlight.airline}
                  onChange={(e) => setReturnFlight((p) => ({ ...p, airline: e.target.value }))}
                  placeholder="Airline"
                  className="px-2 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <input
                  type="text"
                  value={returnFlight.number}
                  onChange={(e) => setReturnFlight((p) => ({ ...p, number: e.target.value }))}
                  placeholder="Flight #"
                  className="px-2 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Departure</label>
                  <input
                    type="datetime-local"
                    value={returnFlight.departure}
                    onChange={(e) => setReturnFlight((p) => ({ ...p, departure: e.target.value }))}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Arrival</label>
                  <input
                    type="datetime-local"
                    value={returnFlight.arrival}
                    onChange={(e) => setReturnFlight((p) => ({ ...p, arrival: e.target.value }))}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Accommodation */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Accommodation (optional)</label>
            <textarea
              value={accommodation}
              onChange={(e) => setAccommodation(e.target.value)}
              placeholder="Hotel name, address, confirmation #, etc."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none"
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleAdd}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium touch-manipulation text-sm"
            >
              Add Trip
            </button>
            <button
              onClick={() => { setAdding(false); resetForm(); }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium touch-manipulation text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-purple-400 hover:text-purple-600 font-medium touch-manipulation text-sm"
        >
          + Add Trip
        </button>
      )}
    </div>
  );
};

export default TripsTab;
